import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShoppingBag, ArrowUpRight, Search, Filter, Loader2, Check, Sparkles, Send } from 'lucide-react';
import BriefingRequestModal from '../components/BriefingRequestModal';
import { geminiService } from '../services/geminiService';
import ContentModal from '../components/ContentModal';
import { AFFILIATE_LINKS } from '../constants/affiliates';
import { cn } from '../lib/utils';

const products = [
  {
    name: "Architectural Silk Collection",
    brand: "Loro Piana",
    category: "Wardrobe",
    image: "https://images.unsplash.com/photo-1618333234901-8581e045a1c3?auto=format&fit=crop&q=80&w=800",
    desc: "The fundamental base layer for a curated executive presence."
  },
  {
    name: "Obsidian Private Ledger",
    brand: "Smythson",
    category: "Logistics",
    image: "https://images.unsplash.com/photo-1544816153-12ad5d7133a2?auto=format&fit=crop&q=80&w=800",
    desc: "For the architectural planning of confidential assets."
  },
  {
    name: "Molecular Restoration Serum",
    brand: "Augustinus Bader",
    category: "Wellness",
    image: "https://images.unsplash.com/photo-1556216785-027581692ceb?auto=format&fit=crop&q=80&w=800",
    desc: "Clinical precision in dermatological structural repair."
  },
  {
    name: "Titanium Travel Architecture",
    brand: "Rimowa",
    category: "Logistics",
    image: "https://images.unsplash.com/photo-1565026057447-bc90a3dceb87?auto=format&fit=crop&q=80&w=800",
    desc: "The gold standard in security-focused global transit."
  }
];

export default function AffiliateCuration() {
  const [procuring, setProcuring] = useState<number | null>(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("All");
  
  // Bespoke AI Curation State
  const [bespokeContext, setBespokeContext] = useState("");
  const [curationLoading, setCurationLoading] = useState(false);
  const [curationContent, setCurationContent] = useState("");
  const [curationModalOpen, setCurationModalOpen] = useState(false);

  const categories = ["All", "Wardrobe", "Logistics", "Wellness"];

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.brand.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "All" || p.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAcquire = (index: number) => {
    setProcuring(index);
    setTimeout(() => {
      setProcuring(null);
      alert("Executive Allocation Requested. Our concierge will contact you for delivery logistics.");
    }, 2000);
  };

  const handleRequestBespoke = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bespokeContext.trim()) return;

    setCurationLoading(true);
    setCurationModalOpen(true);
    try {
      const result = await geminiService.generateCuration(bespokeContext);
      setCurationContent(result);
    } catch (error) {
      console.error(error);
      setCurationContent("Curation link failed. Please retry.");
    } finally {
      setCurationLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-theme-bg">
      <BriefingRequestModal 
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        requestType="Bespoke Curation Allocation"
      />
      <ContentModal
        isOpen={curationModalOpen}
        onClose={() => setCurationModalOpen(false)}
        title="Bespoke Executive Curation"
        category="Cons concierge Allocation"
        content={curationContent}
        isLoading={curationLoading}
      />
      <div className="max-w-7xl mx-auto px-6">
        {/* Strategic Partners Section */}
        <div className="mb-24">
            <span className="text-[10px] uppercase tracking-[0.5em] text-gold mb-8 block text-center">Executive Strategic Partners</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <a 
                  href={AFFILIATE_LINKS.ONLINE_THERAPY} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group relative bg-black/20 border border-gold/30 p-10 hover:border-gold transition-all duration-500 overflow-hidden"
                >
                    <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:opacity-100 transition-opacity">
                        <ArrowUpRight className="h-6 w-6 text-gold" />
                    </div>
                    <div className="relative z-10">
                        <h3 className="font-serif text-2xl mb-4 italic text-theme-text">Online-Therapy.com</h3>
                        <p className="text-taupe text-sm leading-relaxed mb-6 max-w-sm">
                          Our primary referral partner for cognitive behavioral therapy and nervous system stabilization protocols.
                        </p>
                        <span className="text-[10px] uppercase tracking-widest text-gold border-b border-gold/30 pb-1 group-hover:border-gold transition-all">Secure Clinical Allocation</span>
                    </div>
                </a>
                
                <a 
                  href={AFFILIATE_LINKS.AMAZON_STOREFRONT} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group relative bg-black/20 border border-theme-border p-10 hover:border-gold transition-all duration-500 overflow-hidden"
                >
                    <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:opacity-100 transition-opacity">
                        <ArrowUpRight className="h-6 w-6 text-gold" />
                    </div>
                    <div className="relative z-10">
                        <h3 className="font-serif text-2xl mb-4 italic text-theme-text">Executive Library</h3>
                        <p className="text-taupe text-sm leading-relaxed mb-6 max-w-sm">
                          Vetted foundational studies and architectural literature for the high-achieving woman.
                        </p>
                        <span className="text-[10px] uppercase tracking-widest text-theme-text border-b border-theme-border pb-1 group-hover:border-gold group-hover:text-gold transition-all">Access Amazon Curation</span>
                    </div>
                </a>
            </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div>
            <span className="text-[10px] uppercase tracking-[0.5em] text-gold mb-4 block">Boutique Selection</span>
            <h1 className="text-4xl font-serif italic mb-2">Curated Recommendations</h1>
            <p className="text-taupe text-sm italic">Every item is architecturally sound and performance-vetted.</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe" />
                <input
                    type="text"
                    placeholder="Search Curation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-transparent border border-theme-border px-10 py-2 text-xs uppercase tracking-widest text-theme-text focus:outline-none focus:border-gold/50 transition-all w-64"
                />
            </div>
            <div className="flex items-center bg-black/20 border border-theme-border p-1">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={cn(
                      "px-3 py-1 text-[9px] uppercase tracking-widest transition-all",
                      activeCategory === cat ? "bg-gold text-charcoal" : "text-taupe hover:text-theme-text"
                    )}
                  >
                    {cat}
                  </button>
                ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredProducts.map((product, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-theme-bg/30 overflow-hidden"
            >
              <div className="relative aspect-square overflow-hidden mb-6 bg-charcoal border border-theme-border/50 group-hover:border-gold/30 transition-colors duration-500">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
                <div className="absolute top-4 right-4 bg-theme-bg/90 backdrop-blur-sm p-2 border border-theme-border opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <ShoppingBag className="h-4 w-4 text-gold" />
                </div>
              </div>
              <div className="px-1 flex flex-col items-center text-center">
                <span className="text-[9px] uppercase tracking-widest text-gold mb-2">{product.brand}</span>
                <h3 className="text-sm font-medium tracking-wider mb-2">{product.name}</h3>
                <p className="text-xs text-taupe mb-6 leading-relaxed max-w-[200px]">{product.desc}</p>
                <button 
                  onClick={() => handleAcquire(i)}
                  disabled={procuring !== null}
                  className="flex items-center space-x-2 text-[10px] uppercase tracking-[0.3em] text-theme-text border-b border-theme-border pb-1 hover:border-gold hover:text-gold transition-all disabled:opacity-50"
                >
                    {procuring === i ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>Requesting...</span>
                      </>
                    ) : (
                      <>
                        <span>Acquire</span> <ArrowUpRight className="h-3 w-3" />
                      </>
                    )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="py-24 text-center border border-dashed border-theme-border">
            <p className="text-taupe italic py-12">No items match your architectural search criteria.</p>
          </div>
        )}

        {/* Bespoke AI Section */}
        <div className="mt-24 border border-gold/30 bg-gold/5 p-12">
            <div className="max-w-3xl mx-auto text-center">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mx-auto mb-6 border border-gold/20">
                    <Sparkles className="h-5 w-5 text-gold" />
                </div>
                <h2 className="text-2xl font-serif italic mb-4">Request Bespoke AI Curation</h2>
                <p className="text-sm text-taupe mb-10 leading-relaxed italic">
                  Describe a specific life transition or logistical requirement. 
                  Our system will curate a selection of high-end structural assets tailored to your unique context.
                </p>
                <form onSubmit={handleRequestBespoke} className="flex flex-col md:flex-row gap-4">
                  <input 
                    type="text"
                    value={bespokeContext}
                    onChange={(e) => setBespokeContext(e.target.value)}
                    placeholder="e.g. A boardroom wardrobe for a hostile takeover in London..."
                    className="flex-grow bg-theme-bg border border-theme-border px-6 py-4 text-sm font-light italic focus:outline-none focus:border-gold/50 transition-all"
                  />
                  <button 
                    disabled={curationLoading || !bespokeContext.trim()}
                    className="btn-gold flex items-center justify-center space-x-2 px-8 min-w-[200px]"
                  >
                    {curationLoading ? (
                      <span className="animate-pulse">Analyzing...</span>
                    ) : (
                      <>
                        <span>Generate Curation</span> <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
            </div>
        </div>

        <div className="mt-24 p-12 bg-black/20 border border-theme-border text-center">
            <h2 className="text-2xl font-serif italic mb-4">Request a Particular Curation</h2>
            <p className="text-sm text-taupe mb-8 italic">Need a structural recommendation for a specific high-end requirement?</p>
            <button 
              onClick={() => setRequestModalOpen(true)}
              className="btn-outline"
            >Contact Concierge</button>
        </div>
      </div>
    </div>
  );
}
